import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import AgeonLogo from '../components/AgeonLogo';
import EmailDomainField, { buildEmailAddress } from '../components/EmailDomainField';
import { useAuth } from '../store/auth';
import { homePathForUser } from '../utils/workspace';

export default function Login() {
    const [email, setEmail] = useState('');
    const [emailLocal, setEmailLocal] = useState('');
    const [emailDomain, setEmailDomain] = useState('');
    const [domainOptions, setDomainOptions] = useState([]);
    const [enforceDomain, setEnforceDomain] = useState(false);
    const [password, setPassword] = useState('');
    const [showPwd, setShowPwd] = useState(false);
    const [pendingMsg, setPendingMsg] = useState(null);
    const login = useAuth(s => s.login);
    const loading = useAuth(s => s.loading);
    const nav = useNavigate();

    useEffect(() => {
        api.get('/auth/directory')
            .then(r => {
                const domains = r.data?.emailDomains || [];
                setDomainOptions(domains);
                setEnforceDomain(!!r.data?.enforceEmailDomain && domains.length > 0);
                if (domains.length > 0) setEmailDomain(domains[0].slug);
            })
            .catch(() => { });
    }, []);

    async function submit(e) {
        e.preventDefault();
        setPendingMsg(null);
        const resolvedEmail = buildEmailAddress({
            email,
            emailLocal,
            emailDomain,
            enforceDomain,
        });
        if (!resolvedEmail) return toast.error('Enter your work email');
        try {
            const u = await login(resolvedEmail, password);
            toast.success(`Welcome, ${u.name}`);
            nav(homePathForUser(u));
        } catch (err) {
            const r = err.response?.data;
            if (r?.status === 'pending') setPendingMsg('Your account is awaiting admin approval. You\'ll be able to sign in once it\'s approved.');
            if (r?.status === 'rejected') setPendingMsg(`Your account was not approved. ${r.reason || ''}`);
            if (r?.status === 'suspended') setPendingMsg(`Your account is suspended. ${r.reason || ''}`);
        }
    }

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white">
            <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-brand-teal to-brand-teal-700 text-white">
                <AgeonLogo variant="white" />
                <div>
                    <h1 className="text-4xl font-extrabold leading-tight max-w-md">
                        AGEON CRM.<br />For the team behind India&rsquo;s longevity infrastructure.
                    </h1>
                    <p className="mt-4 text-white/80 max-w-md">
                        Centrally managed by admin · approval-gated signup · role-aware experience for sales &amp; care teams.
                    </p>
                </div>
                <div className="text-xs text-white/70">© {new Date().getFullYear()}EBG x AGEON</div>
            </div>

            <div className="flex items-center justify-center p-6 lg:p-10">
                <form onSubmit={submit} className="w-full max-w-sm space-y-5">
                    <div className="lg:hidden mb-6"><AgeonLogo /></div>
                    <div>
                        <h2 className="text-2xl font-bold">Sign in</h2>
                        <p className="text-sm text-ink-500 mt-1">Use your AGEON credentials.</p>
                    </div>

                    {pendingMsg && (
                        <div className="bg-amber-50 text-amber-800 text-sm p-3 rounded-lg border border-amber-200">{pendingMsg}</div>
                    )}

                    <EmailDomainField
                        label="Email"
                        enforceDomain={enforceDomain}
                        domainOptions={domainOptions}
                        email={email}
                        emailLocal={emailLocal}
                        emailDomain={emailDomain}
                        onChange={patch => {
                            if (patch.email !== undefined) setEmail(patch.email);
                            if (patch.emailLocal !== undefined) setEmailLocal(patch.emailLocal);
                            if (patch.emailDomain !== undefined) setEmailDomain(patch.emailDomain);
                        }}
                        required
                        placeholder="you"
                        hint={enforceDomain
                            ? `Use your company domain: ${domainOptions.map(d => '@' + d.slug).join(', ')}`
                            : undefined}
                    />

                    <div>
                        <div className="flex items-center justify-between gap-2">
                            <label className="ag-label mb-0">Password</label>
                            <Link to="/forgot-password" className="text-xs ag-link">Forgot password?</Link>
                        </div>
                        <div className="relative mt-1">
                            <input className="ag-input pr-20" type={showPwd ? 'text' : 'password'}
                                autoComplete="current-password" required
                                value={password} onChange={e => setPassword(e.target.value)} />
                            <button type="button" onClick={() => setShowPwd(v => !v)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs px-2 py-1 rounded bg-ink-100 text-ink-700">
                                {showPwd ? 'Hide' : 'Show'}
                            </button>
                        </div>
                    </div>

                    <button className="ag-btn-primary w-full justify-center" disabled={loading}>
                        {loading ? 'Signing in…' : 'Sign in'}
                    </button>

                    <div className="text-center text-sm text-ink-500">
                        Don&rsquo;t have an account? <Link to="/signup" className="ag-link">Create one</Link>
                    </div>
                    {/* 
                    <p className="text-[11px] text-ink-500 text-center">
                        Admin? Run <code className="px-1 py-0.5 bg-ink-100 rounded">npm run seed</code> in
                        <code className="px-1 py-0.5 bg-ink-100 rounded mx-1">/backend</code> — your one-time password is printed in the terminal.
                    </p> */}
                </form>
            </div>
        </div>
    );
}
